import { useQuery } from '@apollo/client/react'
import { Box, Flex, HStack, Heading, Text, VStack } from '@chakra-ui/react'
import { useMemo } from 'react'

import { SavedViewCard } from '../../components/CardSaveViews/SavedViewCard'
import { Icon } from '../../components/Icons/Icon'
import { Link } from '../../components/Link/Link'
import { SearchTextInput } from '../../components/inputs/SearchTextInput/SearchTextInput'
import { roles } from '../../config/roles'
import {
  CardInfo,
  ChartCard,
  GradientUnderLay,
  SectionCardTitle,
} from '../../pages/Dashboard-parts'
import { prepareViews } from '../../pages/SavedViewsPage/SavedViewsPage.parts'
import { savedViewsListQuery } from '../../services/savedViewService'
import { useAppStore } from '../../store/appStore/appStore'
import type { SavedView } from '../../store/appStore/view/viewSlice.types'
import { colors } from '../../theme/colors'
import { createWidget } from '../widget-utils'

type SearchInputConfig = {
  routeId: string
  placeholder: string
  filterKey: string
}

const searchInputConfigs: SearchInputConfig[] = [
  {
    routeId: 'itemsListView',
    placeholder: 'Item ID',
    filterKey: 'itemId',
  },
  {
    routeId: 'ordersListView',
    placeholder: 'Order ID',
    filterKey: 'purchaseOrderId',
  },
  {
    routeId: 'shipmentsListView',
    placeholder: 'Shipment ID',
    filterKey: 'shipmentId',
  },
  {
    routeId: 'containersListView',
    placeholder: 'Container ID',
    filterKey: 'containerId',
  },
]

const homeDashboardAccentTeal = colors.page.light.homeDashboard.accentTeal

/**
 * Home dashboard body (welcome, search, saved views). Beta users see live
 * snapshot charts via CMS widgets below; others see placeholder tiles here.
 */
const DashboardWidget = () => {
  const userName = useAppStore(store => store.auth.user!.name)
  const hasRole = useAppStore(store => store.auth.hasRole)
  const canViewFclSnapshot = hasRole([roles.betaUser])

  const { data } = useQuery(savedViewsListQuery, {
    variables: { first: 500, skip: 0 },
    fetchPolicy: 'network-only',
  })

  const savedViews = useMemo(() => {
    const items = (data as { SavedView_list?: { items?: unknown[] } })
      ?.SavedView_list?.items
    if (!items?.length) return []
    return prepareViews(items as SavedView[]).sort(
      (a, b) =>
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
    )
  }, [data])

  const hasSavedViews = savedViews.length > 0
  const MAX_VISIBLE = 5
  const hasMoreViews = savedViews.length > 0
  const visibleViews = savedViews.slice(0, MAX_VISIBLE)

  return (
    <VStack width="100%" alignItems="center" data-testid="dashboard-page">
      <VStack maxW="1193px" alignItems="stretch">
        <Box as="section" mt="24px" aria-label="Title Section">
          <VStack alignItems="center" gap="16px">
            <Heading
              color="presets.neutral.100"
              fontSize="28px"
              fontWeight="600"
              letterSpacing="0.84px"
              data-testid="dashboard-welcome-heading"
              _dark={{ color: 'presets.neutral.0' }}
            >
              Welcome, {userName}
            </Heading>
            <Text
              color="presets.neutral.70"
              fontSize="16px"
              fontWeight="500"
              letterSpacing="0.84px"
              _dark={{ color: 'presets.neutral.0' }}
            >
              Supply chain data you can trust
            </Text>
          </VStack>
        </Box>

        <Box as="section" mt="62px" aria-label="Search Inputs Section">
          <HStack gap="25px" justifyContent="stretch">
            {searchInputConfigs.map(({ filterKey, routeId, placeholder }) => {
              const route = useAppStore.getState().router.getRouteById(routeId)
              if (!route) return null

              const queryId = route.viewConfig?.queries?.[0]?.id
              if (!queryId) return null

              return (
                <SearchTextInput
                  key={routeId}
                  route={route}
                  filterKey={filterKey}
                  queryId={queryId}
                  placeholder={placeholder}
                />
              )
            })}
          </HStack>
        </Box>
        <section aria-label="SavedViews Section" style={{ marginTop: '41px' }}>
          <SectionCardTitle>Saved Views</SectionCardTitle>
          {!hasSavedViews ? (
            <VStack alignItems="stretch" mt="18px">
              <CardInfo />
            </VStack>
          ) : (
            <Flex direction="column" gap="10px" mt="18px">
              {visibleViews.map((view, index) => (
                <SavedViewCard
                  key={`${view.viewId}-${view.name}-${index}`}
                  savedView={view}
                />
              ))}
              {hasMoreViews && (
                <Link
                  to="/saved-views"
                  color={homeDashboardAccentTeal}
                  fontSize="12px"
                  fontWeight="700"
                  fontFamily={`'Montserrat', sans-serif`}
                  lineHeight="normal"
                  textDecoration="none"
                  whiteSpace="nowrap"
                  alignSelf="flex-end"
                  _hover={{ textDecoration: 'underline' }}
                >
                  View All
                  <Icon
                    icon="ChevronRightIcon"
                    boxSize="20px"
                    color={homeDashboardAccentTeal}
                    mb="3px"
                  />
                </Link>
              )}
            </Flex>
          )}
        </section>

        <Box as="section" aria-label="Container Snapshot" mt="45px" pb="40px">
          <SectionCardTitle>Container Snapshot</SectionCardTitle>
          {!canViewFclSnapshot ? (
            <VStack gap="40px" mt="18px" alignItems="stretch">
              <HStack spacing="20px" flexWrap="wrap">
                <ChartCard iconName="ShipmentsIcon" graphName="Bars" />
                <ChartCard iconName="ContainersIcon" graphName="Chart" />
              </HStack>
              <HStack spacing="20px" flexWrap="wrap">
                <ChartCard iconName="OrdersIcon" graphName="PieChart" />
                <ChartCard iconName="InboundIcon" graphName="VBar" />
              </HStack>
            </VStack>
          ) : null}
        </Box>

        <GradientUnderLay />
      </VStack>
    </VStack>
  )
}

export const DashboardWidgetDefinition = createWidget(DashboardWidget, {
  type: 'Dashboard',
})
